/**
 * Mirror of backend/lib/testOrders.js — hide CI/sandbox rows in admin UI even if an old API responds.
 */

type SandboxOrderLike = {
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  order_number?: string | null;
  special_instructions?: string | null;
  notes?: string | null;
};

const TEST_CUSTOMER_NAMES = new Set(
  ['integration test customer', 'test customer', 'verify closed day'].map((s) => s.toLowerCase()),
);

const TEST_EMAIL_SNIPPETS = ['mexemexe02@gmail.com', '@test.com', '@example.com'];
const TEST_PHONE_SNIPPETS = ['555-0000'];

export function isSandboxTestOrder(order: SandboxOrderLike | null | undefined): boolean {
  if (!order || typeof order !== 'object') return false;

  const name = String(order.customer_name || '').trim().toLowerCase();
  if (TEST_CUSTOMER_NAMES.has(name)) return true;
  if (name.includes('integration test')) return true;

  const orderNumber = String(order.order_number || '').trim().toUpperCase();
  if (orderNumber.startsWith('TEST-') || orderNumber.startsWith('NBQ-TEST')) return true;

  const notes = `${order.special_instructions || ''} ${order.notes || ''}`.toLowerCase();
  if (notes.includes('fake test order') || notes.includes('test order for confirmation')) {
    return true;
  }

  const email = String(order.customer_email || '').trim().toLowerCase();
  const phone = String(order.customer_phone || '').trim();
  const looksLikeTestEmail = TEST_EMAIL_SNIPPETS.some((s) => email.includes(s));
  const looksLikeTestPhone = TEST_PHONE_SNIPPETS.includes(phone);

  if (
    looksLikeTestEmail &&
    (looksLikeTestPhone || TEST_CUSTOMER_NAMES.has(name) || name.includes('integration test'))
  ) {
    return true;
  }

  return false;
}

export function filterOutSandboxTestOrders<T extends SandboxOrderLike>(orders: T[]): T[] {
  if (!Array.isArray(orders)) return [];
  return orders.filter((o): o is T => !isSandboxTestOrder(o));
}
